import { Button } from "@mui/material"
import cs from "classnames"

import styles from "./PhoneNumbers.module.scss"
import { useContactInfoContext } from "@web/components/EmailCompose/hooks/useGetContactInfo"
import { useCallback, useContext } from "react"
import { PaidFeatureGuardOverlayContext } from "@web/components/FeatureGuard/FeatureGuard"
import { useBoolean } from "@web/hooks/useBoolean"
import { useBilling } from "@web/hooks/useBilling"
import { useMountEffect } from "@react-hookz/web"
import { PricingPage } from "@web/components/PricingPlansNew"
import { PhoneNumbersContent } from "./PhoneNumbersContent"

interface Props {
  isLoading: boolean

  onNumberClick?: (phoneNumber: string) => void
}

export const PhoneNumbers = ({ isLoading, onNumberClick }: Props) => {
  const { lookedUpPhones, canDoContactInfoLookup = true } = useContactInfoContext() ?? {}
  const fetchedPhoneNumber = lookedUpPhones?.[0]?.phoneNumber

  const { OverlayContainer } = useContext(PaidFeatureGuardOverlayContext)
  const { state: isModalOpen, enable: openModal, disable: closeModal } = useBoolean(false)

  const handleNumberClick = useCallback(() => {
    if (!canDoContactInfoLookup) {
      openModal()
      return
    }
    if (!fetchedPhoneNumber || !onNumberClick) return
    onNumberClick(fetchedPhoneNumber)
  }, [fetchedPhoneNumber, onNumberClick, canDoContactInfoLookup, openModal])

  const { billingStatus, getBillingStatus, billingStatusLoading } = useBilling()

  useMountEffect(async () => {
    await getBillingStatus()
  })

  const isButtonDisabled = canDoContactInfoLookup ? isLoading || !fetchedPhoneNumber : false

  return (
    <>
      <Button
        variant="contained"
        disabled={isButtonDisabled}
        className={cs(styles.root, {
          [styles.disabled]: isLoading || !fetchedPhoneNumber,
          ["!tmb-bg-orange-500 !hover:tmb-bg-orange-700"]: !canDoContactInfoLookup,
        })}
        onClick={handleNumberClick}
      >
        <PhoneNumbersContent
          canDoContactInfoLookup={canDoContactInfoLookup}
          isLoading={isLoading}
          fetchedPhoneNumber={fetchedPhoneNumber}
        />
      </Button>
      {isModalOpen && !billingStatusLoading && (
        <OverlayContainer onClose={closeModal}>
          <PricingPage billingProducts={billingStatus?.listProducts ?? []} highlightedProduct="PRO" />
        </OverlayContainer>
      )}
    </>
  )
}
